import { useState } from "react";
import toast from "react-hot-toast";
import { X } from "lucide-react";
import { useCreateWorkbench, useWorkbenchDirectory, WorkbenchVisibility } from "@/api/workbenches";
import { getApiErrorMessage } from "@/api/client";

export default function CreateWorkbenchModal({ onClose }: { onClose: () => void }) {
  const createWorkbench = useCreateWorkbench();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [visibility, setVisibility] = useState<WorkbenchVisibility>("PUBLICA");
  const [memberSearch, setMemberSearch] = useState("");
  const [selectedMembers, setSelectedMembers] = useState<{ id: string; label: string }[]>([]);

  const { data: directory } = useWorkbenchDirectory(memberSearch);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await createWorkbench.mutateAsync({
        name,
        description: description || undefined,
        visibility,
        memberUserIds: visibility === "PRIVADA" ? selectedMembers.map((m) => m.id) : undefined,
      });
      toast.success("Mesa de trabajo creada correctamente.");
      onClose();
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4">
      <div className="card w-full max-w-md p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Nueva mesa de trabajo</h2>
          <button type="button" className="text-gray-400 hover:text-gray-600" onClick={onClose} aria-label="Cerrar">
            <X size={18} />
          </button>
        </div>
        <form className="mt-4 space-y-3" onSubmit={handleSubmit}>
          <div>
            <label htmlFor="workbench-name" className="block text-sm font-medium text-gray-700">
              Nombre
            </label>
            <input
              id="workbench-name"
              className="input-field mt-1"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              minLength={2}
            />
          </div>

          <div>
            <label htmlFor="workbench-description" className="block text-sm font-medium text-gray-700">
              Descripción
            </label>
            <textarea
              id="workbench-description"
              className="input-field mt-1"
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Visibilidad</label>
            <div className="mt-1 flex gap-2">
              <button
                type="button"
                className={`flex-1 rounded-md border px-3 py-2 text-sm font-medium ${
                  visibility === "PUBLICA" ? "border-brand-500 bg-brand-50 text-brand-700" : "border-gray-200 text-gray-600"
                }`}
                onClick={() => setVisibility("PUBLICA")}
              >
                Pública
                <span className="block text-xs font-normal text-gray-400">Visible para todo el equipo</span>
              </button>
              <button
                type="button"
                className={`flex-1 rounded-md border px-3 py-2 text-sm font-medium ${
                  visibility === "PRIVADA" ? "border-brand-500 bg-brand-50 text-brand-700" : "border-gray-200 text-gray-600"
                }`}
                onClick={() => setVisibility("PRIVADA")}
              >
                Privada
                <span className="block text-xs font-normal text-gray-400">Solo para quienes se agreguen</span>
              </button>
            </div>
          </div>

          {visibility === "PRIVADA" && (
            <div>
              <label className="block text-sm font-medium text-gray-700">Miembros iniciales</label>
              {selectedMembers.length > 0 && (
                <div className="mt-1 flex flex-wrap gap-1">
                  {selectedMembers.map((m) => (
                    <span key={m.id} className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-1 text-xs text-gray-700">
                      {m.label}
                      <button
                        type="button"
                        className="text-gray-400 hover:text-red-600"
                        onClick={() => setSelectedMembers((prev) => prev.filter((x) => x.id !== m.id))}
                      >
                        <X size={12} />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <div className="relative mt-1">
                <input
                  className="input-field"
                  placeholder="Buscar por nombre o correo..."
                  value={memberSearch}
                  onChange={(e) => setMemberSearch(e.target.value)}
                />
                {memberSearch && directory && directory.length > 0 && (
                  <div className="absolute z-10 mt-1 w-full rounded-md border border-gray-200 bg-white shadow-lg">
                    {directory
                      .filter((u) => !selectedMembers.some((m) => m.id === u.id))
                      .map((u) => (
                        <button
                          type="button"
                          key={u.id}
                          className="block w-full px-3 py-2 text-left text-sm hover:bg-gray-50"
                          onClick={() => {
                            setSelectedMembers((prev) => [...prev, { id: u.id, label: u.fullName }]);
                            setMemberSearch("");
                          }}
                        >
                          {u.fullName}
                          <span className="ml-1 text-xs text-gray-400">{u.email}</span>
                        </button>
                      ))}
                  </div>
                )}
              </div>
              <p className="mt-1 text-xs text-gray-400">Usted queda automáticamente como propietario.</p>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Cancelar
            </button>
            <button type="submit" className="btn-primary" disabled={createWorkbench.isPending}>
              {createWorkbench.isPending ? "Creando..." : "Crear mesa"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
